<script setup lang="ts">
import { useCommonStore } from '@/stores/modules/common'
import { useUserStore } from '@/stores/modules/user'
import type { NianMonsterRankType } from '@/types/dungeon'
import { formatNumberWithCommas } from '@/utils'
import { socketEmitAsync } from '@/utils/web-socket'
import { onMounted, ref } from 'vue'
import type { TabPaneName } from 'element-plus'

const commonStore = useCommonStore()
const userStore = useUserStore()

const props = defineProps<{
  monsterHp: number
}>()

const curTab = ref('advance')
function handleTabChange(name: TabPaneName) {
  if (name === 'common' && !commonRankList.value?.length) getRank(false)
}
const advanceRankList = ref<NianMonsterRankType[]>([])
const commonRankList = ref<NianMonsterRankType[]>([])
async function getRank(isAdvance: boolean) {
  const res = await socketEmitAsync<NianMonsterRankType[]>({ type: 'getNianMonsterRank', data: { isAdvance } })
  if (isAdvance) advanceRankList.value = res
  else commonRankList.value = res
}
const refreshTime = ref(0)
function handleRefresh() {
  refreshTime.value = 60
  const interval = setInterval(() => {
    refreshTime.value--
    if (refreshTime.value <= 0) clearInterval(interval)
  }, 1000)
  getRank(curTab.value === 'advance')
}

onMounted(() => {
  getRank(true)
})
</script>

<template>
  <div class="rune-tower-rank">
    <h4>共斗年兽排行</h4>
    <el-tabs v-model="curTab" stretch @tab-change="(name) => handleTabChange(name)">
      <el-tab-pane label="进阶" name="advance" class="pane" lazy>
        <p style="display: flex; align-items: center; margin-bottom: 10px">
          <el-button size="small" type="primary" plain @click="handleRefresh" :disabled="refreshTime > 0">
            {{ refreshTime > 0 ? `请等待（${refreshTime}s）` : '刷新' }}
          </el-button>
          <span style="font-size: 12px; margin-left: 10px">* 排名无奖励</span>
        </p>
        <el-table
          :data="advanceRankList"
          border
          style="width: 100%; height: 43vh"
          :row-class-name="({ row }: any) => (row.id === userStore.id ? 'personal' : '')"
          size="small"
          v-loading="commonStore.loading"
        >
          <el-table-column prop="rank" label="排位" width="50">
            <template #default="{ $index }">
              {{ $index + 1 }}
            </template>
          </el-table-column>
          <el-table-column prop="nickname" label="昵称">
            <template #default="{ row }">
              <span class="nickname" :style="{ background: row.color }">{{ row.nickname }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="damage" label="伤害" width="120">
            <template #default="{ row }">
              <span>{{ formatNumberWithCommas(row.damage) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="damage" label="占比" width="70">
            <template #default="{ row }">
              <span>{{ ((row.damage / monsterHp) * 100).toFixed(2) }}%</span>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>
      <el-tab-pane label="普通" name="common" class="pane" lazy>
        <p style="display: flex; align-items: center; margin-bottom: 10px">
          <el-button size="small" type="primary" plain @click="handleRefresh" :disabled="refreshTime > 0">
            {{ refreshTime > 0 ? `请等待（${refreshTime}s）` : '刷新' }}
          </el-button>
          <span style="font-size: 12px; margin-left: 10px">* 排名无奖励</span>
        </p>
        <el-table
          :data="commonRankList"
          border
          style="width: 100%; height: 43vh"
          :row-class-name="({ row }: any) => (row.id === userStore.id ? 'personal' : '')"
          size="small"
          v-loading="commonStore.loading"
        >
          <el-table-column prop="rank" label="排位" width="50">
            <template #default="{ $index }">
              {{ $index + 1 }}
            </template>
          </el-table-column>
          <el-table-column prop="nickname" label="昵称">
            <template #default="{ row }">
              <span class="nickname" :style="{ background: row.color }">{{ row.nickname }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="damage" label="伤害" width="120">
            <template #default="{ row }">
              <span>{{ formatNumberWithCommas(row.damage) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="damage" label="占比" width="70">
            <template #default="{ row }">
              <span>{{ ((row.damage / monsterHp) * 100).toFixed(2) }}%</span>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<style lang="scss" scoped>
.rune-tower-rank {
  :deep() {
    .el-table {
      .personal {
        --el-table-tr-bg-color: var(--el-color-danger-light-7);
      }
    }
  }
}
</style>
